import { SelectItem } from 'primeng/api';
import { BoundingBox, Card, IndImage, IndPhrase } from './cat.model';
import { Env } from './user.model';
import { EmbeddedObjectAnnotation } from './embedded-object-annotation';
import { ConsistencyIssue } from '@shared/components/issues/factory/model/consistency-issue.models';
import { RevRule } from './rev.model';

export class DataElement implements SelectItem {
  id: any;
  text: string;
  selected?: boolean;
  children: DataElement[] = [];

  constructor(id: string = undefined, text: string = undefined) {
    this.id = id ? id.toString() : id;
    this.text = text;
  }

  get label(): string {
    return this.text;
  }

  get value() {
    return this;
  }
}

export class IgnoredWord {
  wordId?: any;
  word: string;  
  isGlobal: boolean; 
  
  constructor(wordId: any = undefined, word: string = undefined, isGlobal: boolean = false) {
    this.wordId = wordId;
    this.word = word;
    this.isGlobal = isGlobal;
  }
}
export class Product implements SelectItem {
  id?: any;
  productDescr?: string;
  selected?: boolean;
  envList?: Env[];
  isEnvDefault?: boolean;
  original?: string;

  constructor(id: string = undefined, productDescr: string = undefined) {
    this.id = id ? id.toString() : id;
    this.productDescr = productDescr;
    this.envList = [];
  }

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value; 
    })   
    }

  restoreOriginal() {
    let original = JSON.parse(this.original) as Product;
    Object.keys(this).forEach((k) => {
      if (k !== 'original') this[k] = original[k];
    });
  }

  isDirty(): boolean {
    let dirty = false;

    let original = JSON.parse(this.original) as Product;
    Object.keys(this).forEach((k) => {
      if (k !== 'original' && this[k] !== original[k]) dirty = true;
    });

    return dirty;
  }

  get label(): string {
    return this.productDescr;
  }

  get value() {
    return this;
  }

  get envListStr(): string {
    return this.envList.map((e) => e.descr).join(', ');
  }
}

export class PaginationParams {
  rangeTo?: number;
  rangeFrom?: number;
  searchId?: number;
  numberOfRows?: number;
}
export class PaginationModel {
  filters?: any[];
  first?: number;
  globalFilter?: any;
  rows?: number;
  sortField?: string;
  sortOrder?: number;
}

export class FileUpload {
  file?: File;
  fileType?: string;
  fileUuid?: string;
  subFolder?: string;
  entityType?: 'doc' | 'instr' = 'doc';
}

export class PresignedUrlResult {
  uploadUrl: string;
  headers: Map<string, string>;
  destFileUrl: string;
  s3Key: string;
}

export class Doc {
  docName: string;
  docS3Key: string;
  pagePdfS3Key: string;
  pagesNum: number;
  pages: DocPage[] = [];
  processStatus: ProcessRunStatusResult;
  docId: number;
  docObjectType: string;
  runNum: number;
  inddS3Key: string;
  statisticsChangedFromPrevious(newStatisticValues): boolean {
    const keys = Object.keys(newStatisticValues);
    let hasDiff = false;
    for (const key of keys) {
      const val1 = (newStatisticValues as any)[key];
      const val2 = (this as any)[key];

      if (val1 != val2) {
        hasDiff = true;
        break;
      }
    }
    return hasDiff;
  }
}

export class DocPage {
  pageNum: number;
  bucket: string;
  s3Key: string;
  url: string;
}

export class FormattedBase {
  dispFormat: string;
  dispFormatList: string[] = [];
  isBold: boolean;
  isItalic: boolean;
  formatDescr: string;
  markDiffSw: boolean;

  get boldSw(): boolean {
    return this.dispFormatList.includes('BOLD') || this.isBold;
  }

  get superscriptSw(): boolean {
    return this.dispFormatList.includes('SUPERSCRIPT');
  }

  get underlineSw(): boolean {
    return this.dispFormatList.includes('UNDERLINE');
  }

  get italicSw(): boolean {
    return this.dispFormatList.includes('ITALIC') || this.isItalic;
  }
}

export class PhraseWord extends FormattedBase {
  id: number;
  text: string;
  chars: WordChar[];
  concatSw: boolean;
  checked: boolean;
  mCardId: number;
}

export class GrammarSpellingIssue {
  id: number;
  type: string;
  description: string;
  replacements: string[];
  offset: number;
  length: number;
  isWhitelisted: boolean;
  isWhitelistedGlobally: boolean;
}

export class ReferenceIssue {
  phraseId: number;
  refPhraseId: number;
  docId: number;
  catPhraseId: number;
  catRefPhraseId: number;
  refPointerWordId: number | null;
  catRefPointerWordId: number | null;
  refFinding: string;
  isPrimary: boolean;
  refChar: string | null;
  similarity: number | null;
  refDiffWords: MatchDiff[] | null;
  refDispWords: PhraseWord[] = [];
  refText: string | null;
  refDocId: number | null;
  originalReferenceIssueText: string | null;
}

export class AssociatedTextFinding {
  phraseId: number;
  assocPhraseId: number;
  docId: number;
  catPhraseId: number;
  catAssocPhraseId: number;
  assocTextFinding: string;
  isPrimary: boolean;
  similarity: number | null;
  assocDiffWords: MatchDiff[] | null;
  assocDispWords: PhraseWord[] = [];
  assocText: string | null;
  originalAssocTextFinding: string;
}

export class AssociatedImageFinding {
  phraseId: number;
  assocImageId: number;
  docId: number;
  catPhraseId: number;
  catAssocImageId: number;
  assocImageFinding: string;
  isPrimary: boolean;
  similarity: number | null;
}

export class WordChar extends FormattedBase {
  id: number;
  text: string;
  dispFormat: string;
}



export class AnchorResult {

  constructor(public issueId: number | null, public refDocAnchorId: number | null, public refDocId: number, public embeddedObjectAnnotation: EmbeddedObjectAnnotation) {
    
  }

  updateAnchorPath(userInput: string, phrase: IndPhrase | RevRule) {
        
    this.embeddedObjectAnnotation.anchorPath = userInput;

    const isDirty = this.embeddedObjectAnnotation.anchorPath !== this.embeddedObjectAnnotation.originalAnchorPath;
    if (isDirty) {
      if (phrase.anchorsPathsToUpdate == null) {
        phrase.anchorsPathsToUpdate = {
          anchors: []
        };
      }
      // CHECK IF ANCHOR ALREADY EXISTS IN THE LIST
      const existingAnchorIndex = phrase.anchorsPathsToUpdate.anchors.findIndex(el => el.ref_doc_anchor_id == this.refDocAnchorId && el.ref_doc_id == this.refDocId);
      // IF NOT EXIST, ADD IT TO THE LIST
      if (existingAnchorIndex === -1) {
        phrase.anchorsPathsToUpdate.anchors.push({
          ref_doc_anchor_id: this.refDocAnchorId,
          ref_doc_id: this.refDocId,
          data: {
            path: this.embeddedObjectAnnotation.anchorPath || ""
          }
        });
      } else {
        // IF EXISTS, UPDATE IT
        phrase.anchorsPathsToUpdate.anchors[existingAnchorIndex] = {
          ref_doc_anchor_id: this.refDocAnchorId,
          ref_doc_id: this.refDocId,
          data: {
            path: this.embeddedObjectAnnotation.anchorPath || ""
          }
        };
      }
    } else {
      // REMOVE ANCHOR IF EXISTS IN THE LIST
      if (phrase.anchorsPathsToUpdate != null && phrase.anchorsPathsToUpdate.anchors.length > 0) {

        phrase.anchorsPathsToUpdate.anchors = phrase.anchorsPathsToUpdate?.anchors.filter(el => el.ref_doc_anchor_id != this.refDocAnchorId || el.ref_doc_id != this.refDocId);
        if (phrase.anchorsPathsToUpdate.anchors.length == 0) {
          phrase.anchorsPathsToUpdate = null;
        }

      }
    }
  }

  remove(phrase: IndPhrase | RevRule) {
    
    phrase.consistencyIssuesToUpdate.remove.phrase_reference_consistency_check_results.push({
      issue_id: this.issueId,
      ref_doc_anchor_id: this.refDocAnchorId,
      ref_doc_id: this.refDocId
    })
  }
}

export class UserAddedAnchorResult extends AnchorResult {

  updateAnchorPath(userInput: string, phrase: IndPhrase | RevRule)  {
            const anchorToUpdate = phrase.consistencyIssuesToUpdate.add?.phrase_reference_consistency_check_results?.find(elem => (elem.ref_doc_id == this.refDocId  && elem.ref_doc_anchor_id == this.refDocAnchorId) ||  (elem.ref_doc_id == this.refDocId && elem.ref_doc_anchor_path == this.embeddedObjectAnnotation?.anchorPath))
            if (anchorToUpdate) {

                anchorToUpdate.ref_doc_anchor_path = userInput;
                this.embeddedObjectAnnotation.anchorPath = userInput;
            }
  }
  remove(phrase: IndPhrase | RevRule) {
        
    const index = phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.findIndex((elem) => (elem.ref_doc_anchor_id == this.refDocAnchorId && elem.ref_doc_id == this.refDocId) || (elem.ref_doc_id == this.refDocId && elem.ref_doc_anchor_path == this.embeddedObjectAnnotation?.anchorPath));

      if (index != -1) {
          phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.splice(index, 1);
      
        }
  }
}

export class BoundingBoxAnchorResult extends AnchorResult {
  
  public phraseIds: number[] | undefined

  public imagesInfos: {
    image_id: number,
    is_table: boolean
  }[] | undefined

  constructor(public issueId: number | null, public refDocAnchorId: number | null, public refDocId: number, public embeddedObjectAnnotation: EmbeddedObjectAnnotation) {
    super(issueId, refDocAnchorId, refDocId, embeddedObjectAnnotation);
  }
  
  updateAnchorPath(userInput: string, phrase: IndPhrase | RevRule)  {
    if (this.refDocAnchorId != null) return;
    this.embeddedObjectAnnotation.anchorPath = userInput;

  }

  remove(phrase: IndPhrase | RevRule) {

    //remove from boundingBoxAnchors array
    phrase.boundingBoxAnchorsToAdd = phrase.boundingBoxAnchorsToAdd.filter((anchor) => anchor !== this);
  }

}
export class CopiedAnchorResult extends AnchorResult {

  constructor(public issueId: number | null, public refDocAnchorId: number | null, public refDocId: number, public embeddedObjectAnnotation: EmbeddedObjectAnnotation) {
    super(issueId, refDocAnchorId, refDocId, embeddedObjectAnnotation);
  }
  
  updateAnchorPath(userInput: string, phrase: IndPhrase | RevRule)  {

/*     this.embeddedObjectAnnotation.anchorPath = userInput;
    const index = phrase.consistencyIssuesToUpdate.add?.phrase_reference_consistency_check_results?.findIndex((elem) => elem.ref_doc_id == this.refDocId && elem.ref_doc_anchor_id == this.refDocAnchorId);
    if (index != -1) {
      phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results[index].ref_doc_anchor_path = userInput;
    }  */
   super.updateAnchorPath(userInput, phrase);

  }

  remove(phrase: IndPhrase | RevRule) {
      
      // at this point phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results must have elements since we copied element to left

      //remove from added anchors
      const index = phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.findIndex((elem) => elem.ref_doc_anchor_id == this.refDocAnchorId && elem.ref_doc_id == this.refDocId);
      if (index != -1) {

        phrase.consistencyIssuesToUpdate.add.phrase_reference_consistency_check_results.splice(index, 1);
      }
  }

}

export class ClaimReferenceAnchorResult extends AnchorResult {

  constructor(public issueId: number | null, public refDocAnchorId: number | null, public refDocId: number, public embeddedObjectAnnotation: EmbeddedObjectAnnotation) {
    super(issueId, refDocAnchorId, refDocId, embeddedObjectAnnotation);  
  }

  remove(phrase: IndPhrase | RevRule) {


  }

  updateAnchorPath(userInput: string, phrase: IndPhrase | RevRule)  {
    
    this.embeddedObjectAnnotation.anchorPath = userInput;

  } 
}

export class ReferencePhrase {
  refPhraseId: number;
  refPhraseText: string;
  refCatMatchTxt: string;
  refType: string;
  phraseId: number;
  similarityPct: number;
  matchSimilarityCode: string;
  extraRefSw: boolean;
  newSw: boolean;
  deleteSw: boolean;
  phraseDispWords: PhraseWord[] = [];
  pointerWordId: number;
  origPointerWordId: number;
  altCatRefPhraseId: number;
  phraseWords: PhraseWord[] = [];
  original: string;
  showInTile: boolean = true;
  showAnchors: boolean = true;
  refDocId: number | null;
  catPhraseId: number;
  anchors: AnchorResult[];
  isConsistencyGenerated: boolean = false;
  isDeletedAnchor: boolean = false;

  saveOriginal() {

    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value; 
    });   
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as ReferencePhrase;
    this.refPhraseText = original.refPhraseText;
    this.refCatMatchTxt = original.refCatMatchTxt;
    this.anchors = original.anchors;
    this.isDeletedAnchor = false;
  }

  isDirty(): boolean {
    if (!this.original) return false;

    let original = JSON.parse(this.original) as ReferencePhrase;
    let textDiff = this.refPhraseText !== original.refPhraseText;
    let catMatchTextDiff = this.refCatMatchTxt !== original.refCatMatchTxt;
    let pointerWordDiff = this.pointerWordId !== original.pointerWordId;
    let consistencyCheckResultDiff = this.anchors?.length !== original.anchors?.length;
    if (!consistencyCheckResultDiff) {
      // we need to check if new anchor result is added (COPIED from right side)
      const currAnchorResults: AnchorResult[] = this.anchors;
      const originalAnchorResults: AnchorResult[] = original.anchors;

      if (currAnchorResults) {

        for (const anchor of currAnchorResults) {
          if (anchor.refDocAnchorId != null) {
            
            // we dont check here bounding box anchors
            const index = originalAnchorResults.findIndex((originalAnchor) => originalAnchor.refDocAnchorId == anchor.refDocAnchorId && originalAnchor.refDocId == anchor.refDocId);
            if (index == -1) {
              
              consistencyCheckResultDiff = true;
              break;
            }
          } else {
            // bounding box anchor added by user
              consistencyCheckResultDiff = true;
              break;
          }
        }
      }
    }
    return textDiff || catMatchTextDiff || pointerWordDiff || consistencyCheckResultDiff;
  }
}

export class AssociatedTextPhrase {
  phraseId: number;
  assocPhraseId: number;
  assocPhraseText: string;
  phraseDispWords: PhraseWord[] = [];
  newSw: boolean;
  deleteSw: boolean;
  showInTile: boolean =  true;
  deAssocOthers = false;

  original: string;
  isInitiallyAssociated: boolean = false;

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value; 
    })     }

  restoreOriginal() {
    let original = JSON.parse(this.original) as AssociatedTextPhrase;
    this.phraseId = original.phraseId;
    this.assocPhraseId = original.assocPhraseId;
    this.assocPhraseText = original.assocPhraseText;
  }
}

export class AssociatedImage {
  phraseId: number;
  assocImageId: number;
  imgUrl: string;
  imgS3Key: string;
  type: any;
  showInTile: boolean = true;
  hideConsistencyIssuesTable: boolean = true;
  consistencyIssues: ConsistencyIssue[];
  
}

export class AddInstr {
  id: number;
  text: string;
  imgUrl: string;
  imgS3Key: string;
  type: any;

  original: string;

  constructor() {
    this.type = { value: 'TXT' };
  }

  saveOriginal() {
    this.original = JSON.stringify(this, (key, value) => {
      if (key === "original") {
         return undefined; // Skip this property
      }
      return value; 
    })   
  }

  restoreOriginal() {
    let original = JSON.parse(this.original) as AddInstr;
    this.text = original.text;
  }

  isDirty(): boolean {
    if (!this.original) return false;

    let original = JSON.parse(this.original) as AddInstr;
    let textDiff = this.text !== original.text;
    return textDiff;
  }
}

export class CatMatch {
  catMatchSeq: number;
  catTxt: string;
  catId: number;
  similarityPct: number;
  phraseSimilarityPct: number;
  diffParts: MatchDiff[];
  catPhrase: IndPhrase;
  catImage: IndImage;
  refMatch: RefMatch[] = [];
  extraReferences: ReferencePhrase[] = [];
  reportDisplaySw: boolean;
  tblRowDiff: TblRowDiff[];
  anchors: string;
  consistencyCheckReason: string;
  consistencyIssueId: number;
  catPhraseId: number;
  catVersionId: number;
  originalConsistencyCheckResult = null;  
  phraseTxt: string;
  maxSimilaritySw: number;
  originalCatMatchReferences: ReferencePhrase[] = [];

    saveOriginal() {

    this.originalConsistencyCheckResult = this.anchors;

    this.catPhrase?.references?.forEach(ref => {
      ref.saveOriginal();
    });
  }
  isDirty(): boolean {

      if (this.anchors !== this.originalConsistencyCheckResult) {
        return true;
      }
      /// return true if any of the references is dirty
      return this.catPhrase?.references?.some(ref => ref.isDirty()) || false;
  }
  restore() {
    this.anchors = this.originalConsistencyCheckResult;
    this.catPhrase?.references?.forEach(ref => {
      ref.restoreOriginal();
    });
  }
}

export class TblRowDiff {
  catRow: TblRow = new TblRow();
  docRow: TblRow = new TblRow();
}

export class TblRow {
  rowId: number;
  cells: TblCell[] = [];
}

export class TblCell {
  cellId: number;
  txt: string;
  diffType: string;
}

export class RefMatch {
  refPhraseId: number;
  catRefPhraseId: number;
  similarityPct: number;
  matchSimilarityCode: string;
  altCatRefPhraseId: number;
}

export class CardEntryMatch {
  card: Card;
  cardEntryId: number;
  catEntry: CatMatch;
}

export class BoundingBoxMatch {
  card: Card;
  boundingBox: BoundingBox;
}

export class MatchDiff {
  id: number;
  text: string;
  diffType: string;
}

export class ProcessRunStatusResult {
  status: ProcessRunStatusEnum;
  errorCode: string;
  errorMsg: string;
  outDocUrl: string;
  outDocS3Key: string;
  completedPct: number;
  steps: ProcessStep[] = [];
  runNum: number;
  createDate: string;
  estimatedTime: number;
  remainingTime : number;

  get isProcessRunning(): boolean {
    return (
      this.status == ProcessRunStatusEnum.RUNNING ||
      this.status == ProcessRunStatusEnum.PENDING ||
      this.status == ProcessRunStatusEnum.PENDING_CHECKS
    );
  }
}

export class ProcessStep {
  stepId: number;
  stepCode: string;
  stepDescr: string;
  stepCategory: string;
}

export class PhraseBorderLine {
  badgeSw: boolean;
  borderLineId: number;
  endX: number;
  endY: number;
  startX: number;
  startY: number;
}

export class PolygonCorner {
  badgeSw: boolean;
  id: number;
  x: number;
  y: number;
}

export class AnnotationElement {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  pageWidth: number;
  pageHeight: number;
  annColor: string;
  annId: number;
  elements: HTMLElement[] = [];
  annSvg: SVGSVGElement;
  annPolygon: SVGPolygonElement;
  annBadge: HTMLElement;
  visible: boolean;
  bgImgSw: boolean;
  borderLines: PhraseBorderLine[] = [];
  polygon: PolygonCorner[] = [];
  awaitLeaveEvent: boolean = false;
  
  private _annIdPrefix: string;
  public get annIdPrefix(): string {
    return this._annIdPrefix;
  }
  public set annIdPrefix(value: string) {
    this._annIdPrefix = value;
  }
}

export class ServerError {
  errorCode?: string;
  message?: string;
  serviceName?: string;
  url?: string;
  req?: string;
  rep?: string;
  userId?: string;
  networkError?: boolean = false;
  isServerError?: boolean = true;
}

export class ServiceCallInfo {
  serviceName?: string;
  url?: string;
  req?: string;
  algServicesSw?: boolean;

  constructor(
    serviceName: string,
    url: string,
    req: string,
    algServicesSw: boolean = false
  ) {
    this.serviceName = serviceName;
    this.url = url;
    this.req = req;
    this.algServicesSw = algServicesSw;
  }
}

export class ConsistencyIssuesToUpdateDto {

  doc_id: number;
  is_precheck: boolean;
  phrase_id: number;
  image_id: number;
  is_table: boolean = false;
  is_ref_consistent_anchor_update_only: boolean = true;
  remove: {
    phrase_reference_consistency_check_results: {
      issue_id: number;
      ref_doc_id: number;
      ref_doc_anchor_id: number
    }[],
    phrase_claim_consistency_check_results: {
      issue_id: number;
      cat_version_id: number;
      cat_phrase_id: number;
    }[],
    phrase_fair_balance_analysis_results: {
      issue_id: number;
    }[],
    phrase_general_compliance_analysis_results: {
      issue_id: number
    }[],
    phrase_assoc_image_claim_consistency_check_results: {
      issue_id: number;
      assoc_image_id: number;
      is_table: boolean
    }[],
    phrase_assoc_image_reference_consistency_check_results: {
      issue_id: number;
      assoc_image_id: number;
      is_table: boolean
    }[],
    phrase_assoc_image_cat_image_consistency_check_results: {
      issue_id: number;
      assoc_image_id: number;
      is_table: boolean
    }[],
    image_claim_consistency_check_results: {
      issue_id: number;
    }[],
    image_reference_consistency_check_results: {
      issue_id: number;
    }[],
    image_cat_image_consistency_check_results: {
      issue_id: number;
    }[]
  };
  convert: { 
    anchors:{      
      issue_id: number;
      ref_doc_id: number;
      ref_doc_anchor_id: number;
      conclusion?: string;
      new_conclusion?: string; 
    }[];
  };
  add: {
    phrase_reference_consistency_check_results: 
        {
            issue_id: number | null,
            ref_doc_id: number,
            ref_doc_anchor_id: number | null,
            ref_doc_anchor_path?: string
        }[]
  
};



}
export class FileValidationParams {
  
  env_id: number;
  source_bucket: string;
  s3_key: string;
}

export class FileValidationResponse {
  
  error_message: string[];
  is_valid: boolean;
}

export class DrawerDetails {

  isImage: boolean;
  phraseTxt: string;
  url: string;
  issueDescription: string;
}

/* == ENUMS == */

export class ProcessRunStatusEnum {
  public static PENDING: string = 'PENDING';
  public static RUNNING: string = 'RUNNING';
  public static PENDING_CHECKS: string = 'PENDING_CHECKS';
  public static FINISHED: string = 'FINISHED';
  public static FAILED: string = 'FAILED';
}

export class MenuItemsEnum {
  static CATALOG: string = 'CATALOG';
  static REVIEW: string = 'REVIEW';
}

export class SearchRunStatusEnum {
  public static STARTED: string = 'STARTED';
  public static RUNNING: string = 'RUNNING';
  public static FINISHED: string = 'FINISHED';
  public static ERROR: string = 'ERROR';
}

export class CatSystemVersionEnum {

  public static OLD_VERSION: number = 1;
}

export function newCatSystemVersion(catSystemVersion?: number | null): boolean {
  
  if (catSystemVersion == null) {
    return false;
  }
  return catSystemVersion > CatSystemVersionEnum.OLD_VERSION;
}